import { useState, useEffect, useMemo } from 'react';
// Remove old hook imports
// import { 
//   useLocations,
//   useDrinks,
//   useBrands,
//   useSightings,
//   useFlavors,
//   useAttributes
// } from '@/hooks/firestore';

// Import RTK hooks and selectors
import { useAppSelector } from "@/store/hooks";
import { 
    selectBrandById,
    selectDrinkById,
    selectFlavorById,
    selectAttributeById,
    selectGlobalDataLoading,
    selectGlobalDataError,
} from "@/store/slices/globalDataSlice";
import { 
    selectLocationById, 
    selectLocationsLoading, 
    selectLocationsError
} from "@/store/slices/locationsSlice";
// Import selector for sighting by ID (assuming it exists or needs to be added)
// For now, we might not be able to get sighting details easily without an ID lookup selector
// Consider if this hook is still needed for sightings, or if context provides enough info.
// import { selectSightingById } from "@/store/slices/sightingsSlice"; // Hypothetical

// Import type from editorSlice (if not defined elsewhere)
import { EditableEntityType } from '@/store/slices/editorSlice';


/**
 * Hook to fetch an entity's name based on its type and ID using RTK store
 */
export function useEntityName(entityType: EditableEntityType | null, entityId: string | null) {
  const [name, setName] = useState<string>('');
  
  // Selectors for individual entities
  const location = useAppSelector((state) => entityType === 'location' && entityId ? selectLocationById(state, entityId) : undefined);
  const drink = useAppSelector((state) => entityType === 'drink' && entityId ? selectDrinkById(state, entityId) : undefined);
  const brand = useAppSelector((state) => entityType === 'brand' && entityId ? selectBrandById(state, entityId) : undefined);
  const flavor = useAppSelector((state) => entityType === 'flavor' && entityId ? selectFlavorById(state, entityId) : undefined);
  const attribute = useAppSelector((state) => entityType === 'attribute' && entityId ? selectAttributeById(state, entityId) : undefined);
  // Sighting lookup might require a dedicated selector or fetching logic if not readily available
  // const sighting = useAppSelector((state) => entityType === 'sighting' && entityId ? selectSightingById(state, entityId) : undefined);
  
  // Select loading/error states
  const globalLoading = useAppSelector(selectGlobalDataLoading);
  const globalError = useAppSelector(selectGlobalDataError);
  const locationsLoading = useAppSelector(selectLocationsLoading);
  const locationsError = useAppSelector(selectLocationsError);
  // Sighting loading/error would depend on how sighting data is managed

  // Determine overall loading state based on relevant entity type
  const isLoading = useMemo(() => {
    switch (entityType) {
      case 'location': return locationsLoading === 'pending';
      case 'brand':
      case 'drink':
      case 'flavor':
      case 'attribute': return globalLoading === 'pending';
      // case 'sighting': return sightingLoading === 'pending'; // Placeholder
      default: return false; // No loading for types without fetched data (confirmation, review, user?)
    }
  }, [entityType, locationsLoading, globalLoading]);

  const error = useMemo(() => {
     switch (entityType) {
      case 'location': return locationsError;
      case 'brand':
      case 'drink':
      case 'flavor':
      case 'attribute': return globalError;
      // case 'sighting': return sightingError; // Placeholder
      default: return null;
    }
  }, [entityType, locationsError, globalError]);

  useEffect(() => {
    let entityName = '';
    if (!entityId || !entityType) {
      setName('');
      return;
    }

    switch (entityType) {
      case 'location':
        entityName = location?.name || `Location ${entityId.substring(0, 6)}..`;
        break;
      case 'drink':
        entityName = drink?.name || `Drink ${entityId.substring(0, 6)}..`;
        break;
      case 'brand':
        entityName = brand?.name || `Brand ${entityId.substring(0, 6)}..`;
        break;
      // case 'sighting': // Needs sighting data access
      //   entityName = `${sighting?.drinkName || 'Unknown'} at ${sighting?.locationName || 'Unknown'}`;
      //   break;
      case 'flavor':
        entityName = flavor?.name || `Flavor ${entityId.substring(0, 6)}..`;
        break;
      case 'attribute':
        entityName = attribute?.name || `Attribute ${entityId.substring(0, 6)}..`;
        break;
      // Simple cases without needing data fetch
      case 'review':
        entityName = `Review ${entityId}`;
        break;
       case 'user': // Assumes user ID is passed
        // Could potentially look up username from authSlice if needed
        entityName = `User ${entityId.substring(0, 6)}..`; 
        break;
      default:
         // Fallback for unhandled or simple types
        entityName = `${entityType || 'Entity'} ${entityId.substring(0, 6)}..`;
    }
    
    // Handle cases where entity exists but name is missing
    if (entityType !== 'review' && entityType !== 'user' && !entityName.includes('..') && ( 
        (entityType === 'location' && location && !location.name) ||
        (entityType === 'drink' && drink && !drink.name) ||
        (entityType === 'brand' && brand && !brand.name) ||
        (entityType === 'flavor' && flavor && !flavor.name) ||
        (entityType === 'attribute' && attribute && !attribute.name)
    )) {
        entityName = `Unnamed ${entityType}`; 
    }

    setName(entityName);

  }, [entityType, entityId, location, drink, brand, flavor, attribute]); // Removed sighting dependency

  return { name, loading: isLoading, error };
} 